import { MdsText, MdsTextSize, MdsTextWeight } from "@/design-system/components/text";
import {
  FooterBackButton,
  FooterButtons,
  FooterContainer,
  FooterNextButton,
  SkipLink,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/ImportWizard/StyledComponents";
import { StepProps } from "@/components/onboarding/components/ImportWizard";
import { DropTargetStep } from "@/components/onboarding/components/ImportWizard/steps/DropTargetStep";
import type { StepMap } from "@/components/onboarding/components/ImportWizard/ImportWizard.types";

const StepTitleCopy =
  "Bring in your Obsidian notes so you can find them with Mem Search, Chat, and Copilot";

const ObsidianStep1 = ({ onNavToStep, onSkip }: StepProps) => (
  <WizardContentWrapper>
    <StepTitle>{StepTitleCopy}</StepTitle>

    <MdsText size={MdsTextSize.Small} weight={MdsTextWeight.SemiBold}>
      Step 1. Find your Obsidian vault
    </MdsText>
    <MdsText size={MdsTextSize.Small}>
      Obsidian stores all your notes in a folder on your computer. This is called a vault.
    </MdsText>

    <MdsText size={MdsTextSize.Small}>
      To find where your vault is on your computer, click the Vault switcher in the lower-left
      corner of Obsidian and select &ldquo;Manage vaults&rdquo;. In the vault manager dialog, your
      vault is listed on the left side. Click the 3 dots menu for options to Reveal your vault on
      your computer.
    </MdsText>

    <img
      src="/onboarding/obsidian-vaults.png"
      alt="Opening the vault manager"
      width="290"
      height="75"
    />

    <ObsidianFooter onNext={() => onNavToStep?.("obsidian-2")} onSkip={onSkip} step={1} />
  </WizardContentWrapper>
);

const ObsidianStep2: React.FC<StepProps> = ({ onNavToStep, onClose, onSkip }) => {
  return (
    <WizardContentWrapper>
      <StepTitle>{StepTitleCopy}</StepTitle>

      <MdsText size={MdsTextSize.Small} weight={MdsTextWeight.SemiBold}>
        Step 2. Import your Obsidian vault into Mem
      </MdsText>
      <MdsText size={MdsTextSize.Small}>
        Import your notes by dragging and dropping the vault folder into the area below.
      </MdsText>

      <DropTargetStep
        onDrop={() => {
          onClose?.();
        }}
      />

      <ObsidianFooter onBack={() => onNavToStep?.("obsidian-1")} step={2} onSkip={onSkip} />
    </WizardContentWrapper>
  );
};

interface ObsidianFooterProps {
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  step: number;
}

const ObsidianFooter = ({ onBack, onNext, onSkip, step }: ObsidianFooterProps) => (
  <FooterContainer>
    <FooterButtons>
      <FooterBackButton onClick={onBack} isDisabled={!onBack} />
      <div>Step {step}/2</div>
      <FooterNextButton onClick={onNext} isDisabled={!onNext} />
    </FooterButtons>
    <div>
      <SkipLink onClick={onSkip}>Skip for now</SkipLink>
    </div>
  </FooterContainer>
);

export const ObsidianStepsMap: StepMap = {
  "obsidian-1": {
    render: ({ onNavToStep, onSkip }) => (
      <ObsidianStep1 onNavToStep={onNavToStep} onSkip={onSkip} />
    ),
  },
  "obsidian-2": {
    render: ({ onNavToStep, onClose, onSkip }) => (
      <ObsidianStep2 onNavToStep={onNavToStep} onClose={onClose} onSkip={onSkip} />
    ),
  },
};
